import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export default function PatientHome() {
  const { user } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/patient/appointments?status=upcoming")
      .then((r) => setUpcoming(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-10" data-testid="patient-home">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ patient</div>
        <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight font-light">
          Hi {user?.full_name?.split(" ")[0]}, what do you need today?
        </h1>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/patient/hospitals" className="group rounded-xl border border-mb-border bg-mb-surface p-6 hover:border-teal-500/30 transition-colors" data-testid="cta-find-doctor">
          <Search className="h-5 w-5 text-teal-400" />
          <div className="mt-4 text-xl tracking-tight">Find a doctor</div>
          <div className="mt-1 text-sm text-mb-fg2">Browse hospitals and book a slot.</div>
          <div className="mt-6 text-sm text-teal-400 inline-flex items-center gap-2">Browse <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></div>
        </Link>
        <Link to="/patient/appointments" className="group rounded-xl border border-mb-border bg-mb-surface p-6 hover:border-teal-500/30 transition-colors" data-testid="cta-my-appointments">
          <CalendarDays className="h-5 w-5 text-teal-400" />
          <div className="mt-4 text-xl tracking-tight">My appointments</div>
          <div className="mt-1 text-sm text-mb-fg2">Upcoming, past, cancelled.</div>
          <div className="mt-6 text-sm text-teal-400 inline-flex items-center gap-2">Open <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></div>
        </Link>
      </div>

      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="upcoming-section">
        <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between">
          <div className="text-sm font-medium">Upcoming</div>
          <Link to="/patient/appointments" className="text-xs text-teal-400 hover:text-teal-300">See all</Link>
        </div>
        <ul className="divide-y divide-mb-border">
          {loading && <li className="px-6 py-8 text-sm text-mb-fg2 text-center">Loading…</li>}
          {!loading && upcoming.length === 0 && (
            <li className="px-6 py-10 text-sm text-mb-fg2 text-center">
              No upcoming appointments. <Link to="/patient/hospitals" className="text-teal-400 hover:text-teal-300">Book your first one</Link>.
            </li>
          )}
          {upcoming.slice(0, 4).map((a) => (
            <li key={a.id} className="px-6 py-4 flex items-center gap-4" data-testid={`upcoming-row-${a.id}`}>
              <div className="h-9 w-9 rounded-full bg-teal-500/10 text-teal-300 flex items-center justify-center text-sm font-medium">
                {(a.doctor?.full_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{a.doctor?.full_name}</div>
                <div className="text-xs text-mb-fg2 truncate">{a.doctor?.specialty} · {a.hospital?.name}</div>
              </div>
              <div className="text-right">
                <div className="text-sm">{new Date(a.slot_start).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                <div className={`text-[10px] uppercase tracking-[0.2em] ${a.status === "confirmed" ? "text-teal-400" : "text-amber-400"}`}>{a.status.replace("_", " ")}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
